import { useMutation, useQuery } from '@api/hooks'
import { ActionCenterTask, ActionCenterTaskType } from '@domain'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Divider,
	Drawer,
	LinearProgress,
	Stack,
	Typography,
} from '@mui/material'
import ActionCenterTasksApi, { ActionCenterKeys } from '@services/actionCenterTasks.api'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import { useQueryClient } from '@tanstack/react-query'
import { useDialogs } from '@toolpad/core'
import PackConvert from '@views/MarketBaskets/ActionCenter/PackConvert/PackConvert'
import { useParams } from 'react-router-dom'
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params'

import { useMarketBasketContext } from '../MarketBaskets.hooks'
import ActionTask from './Components/ActionTask/ActionTask'
import SkipTasksDialog from './Components/SkipTasksDialog/SkipTasksDialog'
import ContractLoading from './ContractLoading/ContractLoading'
import DirectDeals from './DirectDeals/DirectDeals'

type Props = {
	onClose: () => void
}

export default function ActionCenter({ onClose }: Props) {
	const [, setActiveTask] = useQueryParam('active-task', StringParam)
	const [actionCenterVisible] = useQueryParam('action-center', BooleanParam)

	const { marketBasketId } = useParams()
	const dialogs = useDialogs()
	const queryClient = useQueryClient()

	const {
		marketBasket: { skippedTasks },
	} = useMarketBasketContext()

	const contractLoadingEnabled = useBoolVariation('feature-art-1113-contract-loading-task', false)

	const { data } = useQuery({
		queryKey: ActionCenterKeys.tasks(marketBasketId),
		queryFn: () => ActionCenterTasksApi.tasks(marketBasketId),
		enabled: actionCenterVisible,
		// FIXME: remove once web sockets are set up for action center tasks
		refetchInterval: (query) => {
			const tasks = query.state.data as ActionCenterTask[] | undefined
			return tasks?.some((task) => task.isLoading) ? 3000 : false
		},
	})

	const { mutate: skipTasks, isPending: isSkipTasksPending } = useMutation({
		mutationFn: MarketBasketsApi.skipTasks,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: MarketBasketsKeys.marketBasket(marketBasketId) })
			onClose()
		},
	})

	const handleClose = () => {
		queryClient.invalidateQueries({ queryKey: MarketBasketsKeys.marketBasket(marketBasketId) })
		onClose()
	}

	const handleSkipTasks = async () => {
		const confirmation = await dialogs.open(SkipTasksDialog)
		if (confirmation) {
			skipTasks(marketBasketId)
		}
	}

	const handleCloseTask = () => {
		setActiveTask(undefined)
		// Invalidate the tasks query after a short delay to ensure that the BE has finished processing the task if completed
		// The action center tasks cache is updated optimistically on the front end
		setTimeout(() => {
			queryClient.invalidateQueries({ queryKey: ActionCenterKeys.tasks(marketBasketId) })
		}, 3000)
	}

	const filteredTasks = (data ?? []).filter(
		(task) => contractLoadingEnabled || task.task !== ActionCenterTaskType.CONTRACT_LOADING
	)

	if (!actionCenterVisible) return null

	return (
		<>
			<ActionCenterUI
				onClose={handleClose}
				tasks={filteredTasks}
				skippedTasks={skippedTasks}
				isSkipTasksPending={isSkipTasksPending}
				onSkipTasks={handleSkipTasks}
				onTaskClick={(task) => setActiveTask(task)}
			/>

			<DirectDeals onClose={handleCloseTask} />
			<PackConvert onClose={handleCloseTask} />
			{contractLoadingEnabled && <ContractLoading onClose={handleCloseTask} />}
		</>
	)
}

type PropsUi = {
	onClose: () => void | null
	tasks: ActionCenterTask[]
	skippedTasks: boolean
	isSkipTasksPending: boolean
	onSkipTasks: () => void
	onTaskClick: (task: ActionCenterTaskType) => void
}

export function ActionCenterUI({
	onClose,
	tasks,
	skippedTasks,
	isSkipTasksPending,
	onSkipTasks,
	onTaskClick,
}: PropsUi) {
	const completed = tasks.filter((t) => t.completed).length
	const total = tasks.length

	return (
		<Drawer open variant="temporary" anchor="right" onClose={onClose}>
			<Stack sx={{ width: 476, p: 2 }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
					<Typography variant="h6">Complete Setup Tasks</Typography>
					<Button
						variant="outlined"
						color="secondary"
						onClick={onSkipTasks}
						disabled={completed === total || skippedTasks}
						loading={isSkipTasksPending}
						data-testid="action-center-skip-tasks"
					>
						{skippedTasks ? 'Tasks Skipped' : 'Skip Tasks'}
					</Button>
				</Stack>

				<Divider />

				<Alert severity="info" sx={{ my: 2 }}>
					<AlertTitle>Welcome to your New Market Basket</AlertTitle>
					We’ve created a setup checklist to get this basket ready for review!
				</Alert>

				<Typography variant="body2">
					{completed} of {total} tasks complete
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Box sx={{ width: '100%', mr: 1 }}>
						<LinearProgress
							variant="determinate"
							value={total > 0 ? (completed / total) * 100 : 0}
							aria-label="tasks-completed"
						/>
					</Box>
					<Box sx={{ minWidth: 35 }}>
						<Typography variant="body2" sx={{ color: 'text.secondary' }}>
							{`${total > 0 ? Math.round((completed / total) * 100) : 0}%`}
						</Typography>
					</Box>
				</Box>
				<Typography variant="caption" color="secondary" sx={{ mb: 2 }}>
					Complete all tasks to finalize this market basket.
				</Typography>

				{[...tasks]
					.sort((a, b) => a.step - b.step)
					.map((task) => (
						<ActionTask
							key={task.task}
							step={task.step}
							title={task.title}
							subtitle={task.subtitle}
							buttonLabel={task.actionLabel}
							complete={task.completed}
							onClick={() => onTaskClick(task.task)}
							isLoading={task.isLoading}
						/>
					))}
			</Stack>
		</Drawer>
	)
}
